import { useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
// import { queries } from 'services/notifications';
import { useLogoutOneSignalUser } from './oneSignal/useLogoutOneSignalUser';
import { useLoginOneSignalUser } from './oneSignal/useLoginOneSignalUser';
import { PublicUserId } from 'src/services/userService/makePublicUserId';
import { browserIsSafari, userIsOnIosDevice } from '../../util/navigator';
import OneSignal from 'react-onesignal';
import { useRoundtableAuth0 } from '../auth/hooks/useRoundtableAuth0';
import { usePromptForPushNotifications } from './oneSignal/usePromptForPushNotifications';

export function useLogoutUserFromPushNotifications() {
  return useLogoutOneSignalUser();
}

export function usePromptToActivatePushNotifications() {
  const prompt = usePromptForPushNotifications();
  const { isAuthenticated } = useRoundtableAuth0();

  return useCallback(
    async (...params: Parameters<typeof prompt>) => {
      if (!isAuthenticated) {
        return;
      }

      if (OneSignal.Notifications.permission) {
        // user already gave permissions
        return;
      }

      if (!OneSignal.Notifications.isPushSupported()) {
        // push is not supported
        return;
      }
      await prompt(...params);
    },
    [isAuthenticated, prompt]
  );
}

export function useLoginUserForPushNotifications() {
  const loginToOneSignal = useLoginOneSignalUser();
  const prompt = usePromptForPushNotifications();

  const { isAuthenticated } = useAuth0();
  return useCallback(
    async (pubicUserId: PublicUserId) => {
      if (!isAuthenticated) return;

      await loginToOneSignal(pubicUserId);
      // prompt for push notifications if not already
      await prompt();
    },
    [isAuthenticated, loginToOneSignal, prompt]
  );
}

/**
 * @deprecated - noop for now, I think one signal handles this on the backend
 * todo: remove this once we confirm its not needed
 */
export const useSetUserInterestOnPusher = (): {
  updateUserInterests: () => void;
} => {
  /**
   * fetches and sets interests on pusher, avoids setting any state to prevent un-necessary re-renders
   */
  const updateUserInterests = useCallback(async () => {}, []);

  return {
    updateUserInterests,
  };
};

export function useShouldPromptViaUserActionForPushNotifications() {
  const { isAuthenticated } = useRoundtableAuth0();
  return useCallback(() => {
    if (OneSignal.Notifications.permission) {
      // we already have permission - don't prompt
      return false;
    }

    return isAuthenticated && OneSignal.Notifications.isPushSupported();
  }, [isAuthenticated]);
}

export function usePromptNonIosDevicesForPush() {
  const prompt = usePromptToActivatePushNotifications();

  useEffect(() => {
    if (userIsOnIosDevice()) {
      return;
    }

    if (browserIsSafari()) {
      return;
    }

    prompt();
  }, [prompt]);
}
